import { Box, Link, Typography, useTheme } from '@mui/joy';
import { visuallyHidden } from '@mui/utils';
import { useMediaQuery } from '../../hooks';

export type Order = 'asc' | 'desc';

export interface HeadCell<T extends Record<string, string | number>> {
	disablePadding: boolean;
	id: keyof T;
	label: string;
	numeric: boolean;
	sortable?: boolean;
	width?: number;
	numericFormat?: 'currency' | 'percent';
}

export interface DatatableHeaderProps<
	T extends Record<string, string | number>
> {
	onRequestSort: (property: keyof T) => void;
	order: Order;
	orderBy: string;
	headCells: HeadCell<T>[];
}

export const DatatableHeader = <T extends Record<string, string | number>>(
	props: DatatableHeaderProps<T>
) => {
	const { order, orderBy, onRequestSort, headCells } = props;
	const theme = useTheme();

	const matches = useMediaQuery(theme.breakpoints.up('sm'));

	const createSortHandler = (property: keyof T) => () => {
		onRequestSort(property);
	};

	return (
		<thead
			style={{
				backgroundColor: theme.colorSchemes.light.palette.controls.controlFill
			}}
		>
			<tr>
				{headCells.map((headCell) => {
					const active = orderBy === headCell.id;
					const sortable = headCell.sortable ?? true;
					return (
						<th
							key={
								typeof headCell.id === 'string'
									? headCell.id
									: headCell.id.toString()
							}
							aria-sort={
								active
									? ({ asc: 'ascending', desc: 'descending' } as const)[order]
									: undefined
							}
							style={{
								width: headCell.width ? headCell.width : matches ? 200 : 115,
								textWrap: 'wrap',
								whiteSpace: 'normal'
							}}
						>
							<Typography
								level='chartFilter'
								sx={{
									width: '100%',
									flexGrow: 1,
									color: 'white',
									fontWeight: 'normal'
								}}
							>
								{sortable ? (
									<Link
										underline='none'
										textColor={'white'}
										component='button'
										onClick={createSortHandler(headCell.id)}
										fontWeight={active ? 'lg' : 'normal'}
										startDecorator={
											<Box
												sx={{
													display: 'flex',
													flexDirection: 'column',
													width: 12,
													fill: 'white'
												}}
											>
												{active && order !== 'desc' && (
													<svg viewBox='0 0 24 24'>
														<path d='m0 4 12 12 12-12z' />
													</svg>
												)}
												{active && order === 'desc' && (
													<svg viewBox='0 0 24 24'>
														<path d='m0 20 12-12 12 12-12z' />
													</svg>
												)}
												{!active && (
													<svg viewBox='0 0 24 24'>
														<path d='m0 20 12-12 12 12-12z' />
													</svg>
												)}
												{!active && (
													<svg viewBox='0 0 24 24'>
														<path d='m0 4 12 12 12-12z' />
													</svg>
												)}
											</Box>
										}
										sx={{
											'&:hover': { '& svg': { opacity: 1 } },
											textAlign: 'left',
											width: '100%',
											flexGrow: 1
										}}
									>
										{headCell.label}
										{active ? (
											<Box component='span' sx={visuallyHidden}>
												{order === 'desc'
													? 'sorted descending'
													: 'sorted ascending'}
											</Box>
										) : null}
									</Link>
								) : (
									headCell.label
								)}
							</Typography>
						</th>
					);
				})}
			</tr>
		</thead>
	);
};
